import moment from 'moment';
import {isNil, has} from 'lodash';
import {_SYSTEM_TITLES} from '../configuration/index.config';
export const SCHEME_SEPARATOR = '://';
export const PORT_SEPARATOR = ':';

class UtilService {

    /**
     * HSVtoRGB - convert hvs to rgb color
     * @param h - h value
     * @param s - s value
     * @param v - v value
     */
    HSVtoRGB(h, s, v) {
        let r, g, b;
        let newH = h;
        let newS = s;
        let newV = v;

        if (arguments.length === 1) {
            newS = h.s;
            newV = h.v;
            newH = h.h;
        }
        const i = Math.floor(newH * 6);
        const f = newH * 6 - i;
        const p = newV * (1 - newS);
        const q = newV * (1 - f * newS);
        const t = newV * (1 - (1 - f) * newS);

        switch (i % 6) {
            case 0:
                r = newV;
                g = t;
                b = p;
                break;
            case 1:
                r = q;
                g = newV;
                b = p;
                break;
            case 2:
                r = p;
                g = newV;
                b = t;
                break;
            case 3:
                r = p;
                g = q;
                b = newV;
                break;
            case 4:
                r = t;
                g = p;
                b = newV;
                break;
            case 5:
                r = newV;
                g = p;
                b = q;
                break;
            default :
                break;
        }

        return {
            r: Math.round(r * 255),
            g: Math.round(g * 255),
            b: Math.round(b * 255)
        };
    }

    /**
     * _preventDefault -preventDefault function event
     * @param e - click event
     */
    _preventDefault = (e) => e.preventDefault();

    /**
     * _preventWithStopPropagation - preventDefault with stopPropagation functions event
     * @param e - click event
     */
    _preventWithStopPropagation = (e) => {
        e.preventDefault();
        e.stopPropagation();
    }

    /**
     * _preventWithStop - preventDefault with stopImmediatePropagation functions event
     * @param e - click event
     */
    _preventWithStop = (e) => {
        e.preventDefault();
        e.nativeEvent.stopImmediatePropagation();
    }

    /**
     * margeArrayOfArrays - convert Object values to readable structure
     * @param array - Object values
     */
    margeArrayOfArrays = (array) => [].concat.apply([], array);

    /**
     * range - get range array
     * @param start - number
     * @param stop - number
     * @param step - number
     */
    range(start, stop, step) {
        let newStart = start;
        let newStop = stop;
        let newStep = step;

        if (isNil(newStep)) {
            newStop = newStart;
            newStart = 0;
        }

        if (isNil(newStep)) {
            newStep = 1;
        }

        if ((newStep > 0 && newStart >= newStop) || (newStep < 0 && newStart <= newStop)) {
            return [];
        }

        const result:number[] = [];

        for (let i = newStart; newStep > 0 ? i < newStop : i > newStop; i += newStep) {
            result.push(i);
        }

        return result;
    }

    /**
     * random - get random from list
     * @param list - list of items
     */
    random = (list) => list[Math.floor((Math.random() * list.length))];

    /**
     * getBase64FromUrl - get base 64 from link
     * @param url - image link
     */
    getBase64FromUrl = async (url) => {
        const data = await fetch(url);
        const blob = await data.blob();

        return new Promise((resolve) => {
            const reader = new FileReader();

            reader.readAsDataURL(blob);
            reader.onloadend = () => {

                const base64data = reader.result;

                resolve(base64data);
            };
        });
    }

    /**
     * convertSeconds - convert seconds to hours : minutes : seconds
     * @param secs - secs number
     */
    convertSeconds = (secs) => {
        const secNum = parseInt(secs, 10);
        const hours = Math.floor(secNum / 3600);
        const minutes = Math.floor(secNum / 60) % 60;
        const seconds = secNum % 60;

        return [hours, minutes, seconds]
            .map((v) => (v < 10 ? '0' + v : v))
            .filter((v, i) => v !== '00' || i > 0)
            .join(':');
    }

    /**
     * splitArrayToChunks - split array to chunks (matrix)
     * @param arr - array
     * @param numberOfItemsInChunks - number of chunks
     */
    splitArrayToChunks = (arr, numberOfItemsInChunks) => {
        const chunk:any = [];
        let counter = 0;

        arr.forEach((item, i) => {
            if ((i + 1) % numberOfItemsInChunks === 0) {
                chunk.push(arr.slice(counter, i + 1));
                counter = i + 1;
            }
        });

        (chunk.length > 0 && arr.length % numberOfItemsInChunks > 0) && chunk.push(arr.slice(counter, arr.length));
        chunk.length === 0 && chunk.push(arr);

        return chunk;
    };

    /**
     * splitStringIntoChunks - convert string to matrix of strings
     * @param str - the string
     * @param numberOfChunks - number of char per item
     */
    splitStringIntoChunks = (str, numberOfChunks) => str.split('').reduce((resultArray, item, index) => {
        const chunkIndex = Math.floor(index / numberOfChunks);

        !resultArray[chunkIndex] && (resultArray[chunkIndex] = []);
        resultArray[chunkIndex].length === 0 ? resultArray[chunkIndex].push(item) : resultArray[chunkIndex][0] += item;

        return resultArray;
    }, []);

    /**
     * isValidJson - check if json is valid
     * @param json - json
     */
    isValidJson(json) {
        try {
            JSON.parse(json);

            return true;
        } catch (e) {
            return false;
        }
    }

    /**
     * toMatrix - convert array to matrix
     * @param arr - array
     * @param width - length of colum
     */
    toMatrix(arr, width) {
        return arr.reduce(function (rows, key, index) {
            return (index % width === 0 ? rows.push([key])
                : rows[rows.length - 1].push(key)) && rows;
        }, []);
    }

    /**
     * sortByDate - sort list by date
     * @param arr - list
     * @param index - colum
     * @param objKey - sort by which key
     */
    sortByDate = (arr, index, objKey) => arr.sort(function(a, b) {
        const dateA:any = new Date(a[index][objKey].split('/'));
        const dateB:any = new Date(b[index][objKey].split('/'));

        return dateA - dateB;
    })

    /**
     * sortAlphabetically - sort alphabetically
     * @param arr - list
     * @param index - colum
     * @param objKey - sort by which key
     */
    sortAlphabetically = (arr, index, objKey) => arr.sort((a, b) => (`${a[index][objKey]}`.localeCompare(`${b[index][objKey]}`)));

    /**
     * sortDateAndStrings - function that detect between string and dates
     * @param arr - list
     * @param index - colum
     * @param objKey - sort by which key
     */
    sortDateAndStrings = (arr, index, objKey) => (arr.every((obj) => obj[index][objKey].split('/').length === 3) ? this.sortByDate(arr, index, objKey) : this.sortAlphabetically(arr, index, objKey));

    /**
     * normalizedSting - if string is too long add to it ...
     * @param str - the string
     * @param len - max len
     */
    normalizedSting = (str, len) => (str.length > len ? str.slice(0, len) + '...' : str);

    /**
     * normalizedStingWithReturn - if string is too long, slicing it, add ...
     * and return an obj with the original value, the new value, and boolean
     * that represents if the string was sliced.
     * @param originalStr - the string
     * @param len - max len
     * @param cell_ref -ref for element in the screen
     */
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    normalizedStingWithReturn = (originalStr, len, cell_ref: any = null) => {
        const return_obj = {
            originalStr,
            isSliced: false,
            shortStr: ''
        };

        if (cell_ref && cell_ref.current) {
            len = (cell_ref.current.offsetWidth / 9);
        }

        if (originalStr && originalStr.length > len) {
            return_obj['shortStr'] = originalStr.slice(0, len) + '...';
            return_obj['isSliced'] = true;
        }

        return return_obj;
    }

    normalizedStingWidth = (str) => str.slice(0, str.length - 6) + '...'

    /**
     * formatDate - format Date
     * @param date - date
     */
    formatDate = (date) => {
        const d = new Date(date);

        return date ? moment(d).utc().format('MM/DD/YYYY HH:mm') + ' UTC' : '';
    }

    /**
     * formatSize - format Size (KB, MB and etc)
     * @param size - size by bites
     */
    formatSize(size) {
        const sizeBytes = size * (1024 * 1024);
        const i = Math.floor(Math.log(sizeBytes) / Math.log(1024));

        // @ts-ignore
        return (sizeBytes / Math.pow(1024, i)).toFixed(0) * 1 + ['B', 'kB', 'MB', 'GB', 'TB'][i];
    }

    /**
     * checkIfArrayIsDifferent - equal between two arrays
     * @param arr1
     * @param arr2
     */
    checkIfArrayIsDifferent = (arr1, arr2) => {
        if (arr2.length > 0 && arr1.length !== arr2.length) {
            return true;
        }

        return arr2.length > 0 && arr1.join('') !== arr2.join('');

    };

    /**
     * nFormatter - format number like 1k 10m...
     * @param num - number
     */
    nFormatter(num) {
        if (num >= 1000000000) {
            return `${(num / 1000000000)}`.replace(/\.0$/, '') + 'G';
        }
        if (num >= 1000000) {
            return `${(num / 1000000)}`.replace(/\.0$/, '') + 'M';
        }
        if (num >= 1000) {
            return `${(num / 1000)}`.replace(/\.0$/, '') + 'K';
        }

        return num;
    }

    /**
     * changeKeyToValues - changeKeyToValues
     * @param array - array
     * @param key - key to change
     */
    changeKeyToValues(array, key) {
        return array.map((obj) => {
            const newObj = {};

            for (const objKey in obj) {
                if (has(obj, objKey)) {
                    newObj[`${key}_${objKey}`] = obj[objKey];
                }
            }

            return newObj;
        });
    }

    /**
     * changeKeyToClientKeys - convert from above function
     * @param array - array
     */
    changeKeyToClientKeys(array) {
        const newArray:any = [];
        const copyArray = JSON.parse(JSON.stringify(array));

        copyArray.forEach((obj) => {
            const newObj = {};

            for (const objKey in obj) {
                if (has(obj, objKey)) {
                    const newKey = objKey.split('_')[1];

                    newObj[newKey] = obj[objKey];
                }
            }

            newArray.push(newObj);
        });

        return newArray;
    }

    sendAnnotationsToServer(data) {
        data.tags = this.changeKeyToValues(data.tags, 'tag');
        data.attributes = this.changeKeyToValues(data.attributes, 'attribute');
        data.labels = this.changeKeyToValues(data.labels, 'label');

        return data;
    }

    setAnnotationsToClient(data) {
        const tags = this.changeKeyToClientKeys(data.tags);
        const attributes = this.changeKeyToClientKeys(data.attributes);
        const labels = this.changeKeyToClientKeys(data.labels);

        return {...data, tags, attributes, labels};
    }
    getElementsByText() {
        const spans = document.getElementsByTagName('p');
        const results:string[] = [];

        for (let i = 0; i < spans.length; i++) {
            const content:string = spans[i].textContent || spans[i].innerText || '';

            results.push(content);
        }

        return results;
    }
    capitalize = (string) => string[0].toUpperCase() + string.slice(1);
    removeLastChar = (str, i = 1) => str.substring(0, str.length - i);
    maxArrayObj = (a: any[], f: string | number) => a.reduce((m, x) => (m[f] > x[f] ? m : x));
    minArrayObj= (a, f) => a.reduce((m, x) => (m[f] < x[f] ? m : x));
    makeSystemTitle = (vale) => _SYSTEM_TITLES[vale]
    normalizedToTable = (array, propertiesKeys) => {
        const copyTableList:any = [];

        array.forEach((obj) => {
            const row:any = [];

            propertiesKeys.forEach((property:any) => {
                const rowObj: any = {};

                property.key && (rowObj[property.key] = property.func ? property.func(property.value ? obj[property.value] : obj) : obj[property.value]);
                obj.id && (rowObj.id = obj.id);
                property.marginLeft && (rowObj.marginLeft = property.marginLeft);
                property.size && (rowObj.size = property.size);
                property.maxLenString && (rowObj.maxLenString = property.maxLenString);
                property.color && (rowObj.color = property.color);
                property.objKey && obj[property.objKey] && (rowObj.childrenObj = obj[property.objKey]);
                property.edit && (rowObj.edit = property.edit);
                property.imageIcon && (rowObj.imageIcon = property.imageIcon(obj));
                property.className && (rowObj.className = property.className);
                property.mandatory && (rowObj.mandatory = property.mandatory);
                property.minWidth && (rowObj.minWidth = property.minWidth);
                property.isAcProject && (rowObj.isAcProject = property.isAcProject);
                row.push(rowObj);
            });

            copyTableList.push(...row);
        });

        return copyTableList;
    }

    calculateMaxCharacters = (text, maxWidth, textRef) => {
        const span = document.createElement('span');
        const computedStyle = getComputedStyle(textRef.current);

        span.style.visibility = 'hidden';
        span.style.font = computedStyle.font;

        document.body.appendChild(span);

        let maxChars = 0;
        let currentText = '';

        for (let i = 0; i < text.length; i++) {
            const char = text[i];

            currentText += char;
            span.textContent = currentText;

            const { width } = span.getBoundingClientRect();

            if (width > maxWidth) {
                break;
            }

            maxChars++;
        }

        document.body.removeChild(span);

        return maxChars;
    }
    findKeyInArrayOfObj(array, key) {
        const results:number[] = [];

        array.forEach((item, index) => {
            if (key in item) {
                results.push(index);
            }
        });

        return results;
    }

    cutTextToFitWidth = (text, maxWidth, textRef) => {
        const ellipsis = '...';

        return text.substring(0, this.calculateMaxCharacters(text, maxWidth, textRef) - 3) + ellipsis;
    }

    getSubDomainFromUrl = (url) => {
        if (!url) {
            return url;
        }

        try {
            let domain = url.split('.')[0];

            const schemeIndex = domain.indexOf(SCHEME_SEPARATOR);

            if (schemeIndex >= 0) {
                domain = domain.substring(schemeIndex + SCHEME_SEPARATOR.length);
            }

            if (domain.indexOf(PORT_SEPARATOR) >= 0) {
                domain = domain.split(PORT_SEPARATOR)[0];
            }

            return domain;
        } catch (err) {
            return '';
        }
    }

    removeParamFromUrl = (paramName, history) => {
        const url = new URL(window.location.href);

        url.searchParams.delete(paramName);
        const pathArray = url.pathname.split('/');
        const pathFilter = [...new Set(pathArray.filter((segment) => segment !== 'vis'))];
        const newPath = pathFilter.join('/');

        history.replace(`${newPath}${url.search}`);
    }

    findLastRoute = () => {
        const pathArray = window.location.pathname.split('/');

        return pathArray.pop();
    }

    replaceLastRoute = (routeName, num_of_pop = 1) => {
        const pathArray = window.location.pathname.split('/');

        for (let i = 0; i < num_of_pop; i++) {
            pathArray.pop();
        }
        const pathFilter = pathArray.filter((segment) => segment !== 'vis');

        return pathFilter.join('/') + `/${routeName}`;
    }
}

export default new UtilService();
